import { Field, Select, Switch } from '@bb/ui';
import { useState, type ReactNode } from 'react';
import { environmentLocales, isRtlLocale, previewContent, type ChannelContent, type LocaleSettings } from '../shared/localization.ts';
import { effectiveContent, PLATFORM_LABELS, type PushContentInput, type PushPlatform } from './pushDraft.ts';
import { bodyLimitOf, PREVIEW_SURFACES, truncateGraphemes, type AndroidSurface, type IosSurface, type PreviewDevice, type PreviewSurface, type PreviewTheme } from './pushLength.ts';

/**
 * Push previews (BB-73): the system look of a notification — iOS lock screen, banner and Notification Center; Android
 * collapsed, expanded and lock screen — on a phone or a tablet, light or dark. Texts are cut where the recommended
 * limits say (`PUSH_LENGTH_GUIDANCE`), titles stay on one line. Layout only: OEM skins differ, so a test send on a
 * real device stays the check (BB-59). Templates are shown as written; the Test tab renders them for a user.
 */
export interface NotificationText {
  title?: string;
  subtitle?: string;
  body?: string;
  summary?: string;
  imageUrl?: string;
  buttons?: string[];
}

export interface NotificationPreviewProps {
  text: NotificationText;
  device?: PreviewDevice;
  theme?: PreviewTheme;
  silent?: boolean;
  /** Shown as the sending app. */
  appName?: string;
  lang?: string;
}

const DEVICE_LABELS: Record<PreviewDevice, string> = { phone: 'Phone', tablet: 'Tablet' };
const oneLine = (text: string) => text.replace(/[\r\n]+/g, ' ');
/** Templated or non-https images are not fetched: a placeholder takes their place. */
const loadable = (url: string) => /^https:\/\/[^\s{}]+$/.test(url);

function Thumb({ url, large = false }: { url: string; large?: boolean }) {
  const className = large ? 'push-notification__picture' : 'push-notification__thumb';
  return loadable(url) ? <img className={className} src={url} alt="Notification image" /> : <span className={`${className} push-notification__placeholder`} role="img" aria-label="Notification image (template)" />;
}

function Body({ text, limit }: { text: string; limit: number }) {
  const cut = truncateGraphemes(text, limit);
  return (
    <p className="push-notification__body" data-truncated={cut.truncated || undefined}>
      {cut.text}
    </p>
  );
}

function Frame({ platform, surface, device, theme, children, label }: { platform: PushPlatform; surface: string; device: PreviewDevice; theme: PreviewTheme; children: ReactNode; label: string }) {
  return (
    <figure className={`push-device push-device--${platform} push-device--${device} push-device--${theme} push-device--${surface}`} aria-label={label}>
      <div className="push-device__status" aria-hidden="true">
        <span>9:41</span>
        {platform === 'ios' && <span className="push-device__notch" />}
        <span>●●● ▮</span>
      </div>
      <div className="push-device__screen">{children}</div>
    </figure>
  );
}

function LockClock({ platform }: { platform: PushPlatform }) {
  return (
    <div className={`push-device__clock push-device__clock--${platform}`} aria-hidden="true">
      <span className="push-device__date">Monday, 16 October</span>
      <span className="push-device__time">9:41</span>
    </div>
  );
}

const silentNote = <p className="push-device__silent">Silent push: nothing is shown, the app is woken up in the background.</p>;

/** An iOS notification as the system draws it. */
export function IosNotificationPreview({ text, surface, device = 'phone', theme = 'light', silent = false, appName = 'VCG Markets', lang }: NotificationPreviewProps & { surface: IosSurface }) {
  const limit = bodyLimitOf('ios', surface, Boolean(text.imageUrl));
  const label = `iOS ${PREVIEW_SURFACES.ios.find((s) => s.value === surface)?.label ?? surface} preview, ${DEVICE_LABELS[device].toLowerCase()}, ${theme}`;
  const card = silent ? (
    silentNote
  ) : (
    <article className="push-notification push-notification--ios" aria-label="Notification" lang={lang} dir={lang && isRtlLocale(lang) ? 'rtl' : undefined}>
      <div className="push-notification__main">
        <span className="push-notification__icon" aria-hidden="true" />
        <div className="push-notification__text">
          <div className="push-notification__head">
            <span className="push-notification__title">{text.title ? oneLine(text.title) : appName}</span>
            <span className="push-notification__time">now</span>
          </div>
          {text.subtitle && <p className="push-notification__subtitle">{oneLine(text.subtitle)}</p>}
          {text.body && <Body text={text.body} limit={limit} />}
        </div>
        {text.imageUrl && <Thumb url={text.imageUrl} />}
      </div>
    </article>
  );
  return (
    <Frame platform="ios" surface={surface} device={device} theme={theme} label={label}>
      {surface === 'lock_screen' && <LockClock platform="ios" />}
      {surface === 'notification_center' && <p className="push-device__heading">Notification Center</p>}
      {card}
      {surface === 'banner' && <div className="push-device__app" aria-hidden="true" />}
    </Frame>
  );
}

/** An Android notification as the system draws it (the VCG app uses the system template). */
export function AndroidNotificationPreview({ text, surface, device = 'phone', theme = 'light', silent = false, appName = 'VCG Markets', lang }: NotificationPreviewProps & { surface: AndroidSurface }) {
  const expanded = surface === 'expanded';
  const limit = bodyLimitOf('android', surface, Boolean(text.imageUrl));
  const label = `Android ${PREVIEW_SURFACES.android.find((s) => s.value === surface)?.label ?? surface} preview, ${DEVICE_LABELS[device].toLowerCase()}, ${theme}`;
  const card = silent ? (
    silentNote
  ) : (
    <article className={`push-notification push-notification--android${expanded ? ' push-notification--expanded' : ''}`} aria-label="Notification" lang={lang} dir={lang && isRtlLocale(lang) ? 'rtl' : undefined}>
      <div className="push-notification__head">
        <span className="push-notification__icon push-notification__icon--small" aria-hidden="true" />
        <span className="push-notification__app">{appName}</span>
        {expanded && text.summary && <span className="push-notification__summary">· {oneLine(text.summary)}</span>}
        <span className="push-notification__time">· now</span>
        <span className="push-notification__chevron" aria-hidden="true">
          {expanded ? '⌃' : '⌄'}
        </span>
      </div>
      <div className="push-notification__main">
        <div className="push-notification__text">
          {text.title && <p className="push-notification__title">{oneLine(text.title)}</p>}
          {text.body && <Body text={text.body} limit={limit} />}
        </div>
        {text.imageUrl && !expanded && <Thumb url={text.imageUrl} />}
      </div>
      {text.imageUrl && expanded && <Thumb url={text.imageUrl} large />}
      {expanded && text.buttons && text.buttons.length > 0 && (
        <div className="push-notification__buttons">
          {text.buttons.map((b, i) => (
            <span key={`${i}/${b}`} className="push-notification__button">
              {b}
            </span>
          ))}
        </div>
      )}
    </article>
  );
  return (
    <Frame platform="android" surface={surface} device={device} theme={theme} label={label}>
      {surface === 'lock_screen' && <LockClock platform="android" />}
      {surface !== 'lock_screen' && <div className="push-device__shade-head" aria-hidden="true" />}
      {card}
    </Frame>
  );
}

/** What one platform shows of a content (overrides applied), with the Android button labels. */
export function notificationText(content: PushContentInput, platform: PushPlatform): NotificationText {
  const e = effectiveContent(content, platform);
  return {
    ...(e.title ? { title: e.title } : {}),
    ...(e.subtitle ? { subtitle: e.subtitle } : {}),
    ...(e.body ? { body: e.body } : {}),
    ...(e.summary ? { summary: e.summary } : {}),
    ...(e.imageUrl ? { imageUrl: e.imageUrl } : {}),
    ...(platform === 'android' && content.androidButtons?.length ? { buttons: content.androidButtons.map((b) => b.label) } : {}),
  };
}

export type PreviewSurfaces = { ios: IosSurface; android: AndroidSurface };
export const DEFAULT_SURFACES: PreviewSurfaces = { ios: 'lock_screen', android: 'collapsed' };

export interface PushPreviewPaneProps {
  /** Platforms the message is written for, previewed one under the other. */
  platforms: readonly PushPlatform[];
  /** The content each platform gets (shared or its own). */
  contentOf: (platform: PushPlatform) => PushContentInput;
  /** The environment's locales: a preview locale is offered when there are several. */
  locales?: LocaleSettings;
  /** The notification state of each platform; the composer's length counters follow it. */
  surfaces: PreviewSurfaces;
  onSurfacesChange: (next: PreviewSurfaces) => void;
}

/** The composer's preview column: per platform a device and a notification state, one theme and one locale for all. */
export function PushPreviewPane({ platforms, contentOf, locales, surfaces, onSurfacesChange }: PushPreviewPaneProps) {
  const [devices, setDevices] = useState<Record<PushPlatform, PreviewDevice>>({ ios: 'phone', android: 'phone' });
  const [theme, setTheme] = useState<PreviewTheme>('light');
  const codes = locales ? environmentLocales(locales).codes : [];
  const [locale, setLocale] = useState('');
  const shownLocale = codes.includes(locale) ? locale : (codes[0] ?? '');

  return (
    <section className="push-preview-pane" aria-label="Notification preview">
      <div className="toolbar">
        <Switch label="Dark theme" checked={theme === 'dark'} onCheckedChange={(dark) => setTheme(dark ? 'dark' : 'light')} />
        {codes.length > 1 && (
          <Field label="Preview locale">
            <Select options={codes.map((c, i) => ({ value: c, label: i === 0 ? `${c} (default)` : c }))} value={shownLocale} onChange={(e) => setLocale(e.target.value)} />
          </Field>
        )}
      </div>
      {platforms.map((platform) => {
        const content = contentOf(platform);
        const merged = locales && shownLocale ? previewContent(content as ChannelContent, shownLocale, locales) : { content: content as ChannelContent, locale: shownLocale };
        const text = notificationText(merged.content as PushContentInput, platform);
        const silent = content.notificationType === 'silent';
        const device = devices[platform];
        const surface = surfaces[platform];
        const setSurface = (value: PreviewSurface) => onSurfacesChange({ ...surfaces, [platform]: value } as PreviewSurfaces);
        return (
          <div key={platform} className="push-preview-pane__platform" role="group" aria-label={`${PLATFORM_LABELS[platform]} preview`}>
            <h4>{PLATFORM_LABELS[platform]}</h4>
            <div className="audience-filter">
              <Field label={`${PLATFORM_LABELS[platform]} device`}>
                <Select options={(['phone', 'tablet'] as const).map((d) => ({ value: d, label: DEVICE_LABELS[d] }))} value={device} onChange={(e) => setDevices((was) => ({ ...was, [platform]: e.target.value as PreviewDevice }))} />
              </Field>
              <Field label={`${PLATFORM_LABELS[platform]} notification state`}>
                <Select options={PREVIEW_SURFACES[platform]} value={surface} onChange={(e) => setSurface(e.target.value as PreviewSurface)} />
              </Field>
            </div>
            {shownLocale && merged.locale !== shownLocale && <p className="hint">No {shownLocale} translation: shown in {merged.locale}.</p>}
            {platform === 'ios' ? (
              <IosNotificationPreview text={text} surface={surfaces.ios} device={device} theme={theme} silent={silent} lang={merged.locale || undefined} />
            ) : (
              <AndroidNotificationPreview text={text} surface={surfaces.android} device={device} theme={theme} silent={silent} lang={merged.locale || undefined} />
            )}
          </div>
        );
      })}
      <p className="hint">Always test on a real device: OEM skins and settings change how notifications look.</p>
    </section>
  );
}
